import React from "react";
import { Navigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";
import { BottomNav } from "./BottomNav";
import { SEO } from "@/components/shared/SEO";
import { Radar, ShoppingBag, ExternalLink } from "lucide-react";

interface DashboardLayoutProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  children: React.ReactNode;
  seoPath?: string;
  seoDescription?: string;
  seoKeywords?: string[];
  /** Authenticated app pages are noindex by default (private workspace routes). */
  seoNoindex?: boolean;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({
  title,
  subtitle,
  actions,
  children,
  seoPath,
  seoDescription,
  seoKeywords,
  seoNoindex = true,
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Kiosk mode: `?kiosk=1` hides sidebar, top bar, footer, bottom nav — the
  // dashboard body fills the projector screen edge-to-edge for demos.
  const kiosk = React.useMemo(
    () => new URLSearchParams(location.search).get("kiosk") === "1",
    [location.search],
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[color:var(--sg-bg)]">
        <div className="sg-skeleton h-6 w-40" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  if (kiosk) {
    return (
      <div className="min-h-screen bg-[color:var(--sg-bg)]" data-testid="dashboard-kiosk">
        <SEO
          title={title}
          description={seoDescription || subtitle || `${title} — SociBaba workspace.`}
          keywords={seoKeywords}
          path={seoPath}
          noindex={seoNoindex}
        />
        <div className="px-6 md:px-10 py-5 flex items-center justify-between border-b border-[color:var(--sg-border)]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] font-bold text-[color:var(--sb-saffron,#F97316)]">Kiosk · Demo Mode</div>
            <h1 className="font-serif-display text-2xl font-bold text-[color:var(--sg-fg)] mt-0.5">{title}</h1>
            {subtitle && <p className="text-sm text-[color:var(--sg-muted)] mt-0.5">{subtitle}</p>}
          </div>
          <Link
            to={location.pathname}
            data-testid="kiosk-exit"
            className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[color:var(--sg-border)] text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)] hover:bg-[color:var(--sg-muted-bg)]"
          >
            Exit kiosk ✕
          </Link>
        </div>
        <div className="px-6 md:px-10 py-6">{children}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-[color:var(--sg-bg)]">
      <SEO
        title={title}
        description={seoDescription || subtitle || `${title} — SociBaba workspace.`}
        keywords={seoKeywords}
        path={seoPath}
        noindex={seoNoindex}
      />
      <Sidebar />
      <main className="flex-1 min-w-0 flex flex-col">
        <TopBar title={title} subtitle={subtitle} actions={actions} />
        <div className="px-4 sm:px-6 md:px-10 py-6 sm:py-8 flex-1">{children}</div>
        <div className="px-4 sm:px-6 md:px-10 py-4 border-t border-[color:var(--sg-border)] flex flex-wrap items-center justify-between gap-2 text-xs text-[color:var(--sg-muted)] bg-[color:var(--sg-card)]" data-testid="dashboard-footer">
          <div className="flex items-center gap-3">
            <span>© {new Date().getFullYear()} SociBaba</span>
            <span className="hidden sm:inline text-[color:var(--sg-muted)]/70">·</span>
            <span className="hidden sm:inline italic tracking-wide">Signal → Action · Turn every comment into a lead, a fix, or a decision.</span>
            <a
              href="/ai-cart"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="footer-siba-score"
              className="inline-flex items-center gap-1 hover:text-[color:var(--sg-fg)]"
            >
              <Radar className="h-3.5 w-3.5" />
              SIBA Score
              <ExternalLink className="h-3 w-3 opacity-70" />
            </a>
            <a
              href="/shop"
              target="_blank"
              rel="noreferrer noopener"
              data-testid="footer-shop"
              className="inline-flex items-center gap-1 hover:text-[color:var(--sg-fg)]"
            >
              <ShoppingBag className="h-3.5 w-3.5" />
              Shop
              <ExternalLink className="h-3 w-3 opacity-70" />
            </a>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event("sb:open-cmdk"))}
              className="hidden md:inline-flex items-center gap-1.5 hover:text-[color:var(--sg-fg)]"
              data-testid="footer-cmdk-hint"
              aria-label="Open command palette"
            >
              <span>Press</span>
              <kbd className="text-[10px] font-mono bg-[color:var(--sg-card)] border border-[color:var(--sg-border)] rounded px-1.5 py-0.5">
                {typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘K" : "Ctrl+K"}
              </kbd>
              <span>to jump anywhere</span>
            </button>
            <Link to="/privacy" className="hover:text-[color:var(--sg-fg)]">Privacy</Link>
            <Link to="/terms" className="hover:text-[color:var(--sg-fg)]">Terms</Link>
            <Link to="/cookies" className="hover:text-[color:var(--sg-fg)]">Cookies</Link>
            <Link to="/refunds" className="hover:text-[color:var(--sg-fg)]">Refunds</Link>
            <Link to="/data-deletion" className="hover:text-[color:var(--sg-fg)]">Data deletion</Link>
            <Link to="/security" className="hover:text-[color:var(--sg-fg)]">Security</Link>
            <Link to="/contact" className="hover:text-[color:var(--sg-fg)]">Contact</Link>
          </div>
        </div>
        <BottomNav />
      </main>
    </div>
  );
};
